import React, { Suspense, useCallback, useContext, useEffect, useRef, useState } from "react";

import ChatProvider, { OPEN_REPORT_PANEL_EVENT, WithChatContext } from "../../services/chat-provider"; // legacy import
import UniversalChatProvider from "../../services/chat-provider";
import ChatSidebar from "../../components/chat-sidebar/ChatSidebar";
import ChatPageHeader from "../../components/chat-sidebar/ChatPageHeader";
import ChatContent from "../../components/chat-content/ChatContent";
import { useChatContext } from "../../services/chat-provider";
import ChatSessionInfo from "../../components/chat-session-info";
import ChatReportPanel from "../../components/chat-report-panel";
import CanvasPanel from "../../components/canvas/CanvasPanel";
import ReportFormDialog from "../../components/chat-report-panel/ReportFormDialog";
import AnokyeContent from "../../components/anokye-content/AnokyeContent";
import ReportHistory from "../../components/anokye-content/pages/reports-history/ReportHistory";
import ReportOverview from "../../components/anokye-content/pages/report-overview/ReportOverview";
import PageLoading from "@minohealth/common/components/page.loading/PageLoading";
import SourcesSidebar from "../../components/chat-bubble/SourcesSidebar";
import { Route, Switch, useHistory, Redirect } from "react-router-dom";
import NotFound from "../not-found";
import { useSelector } from "react-redux";
import { showConfirm } from "@minohealth/common/utils";
import { ResponsiveLayoutProvider } from "../../contexts/ResponsiveLayoutContext";
import SplitPane from "react-split-pane";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";

import mixpanel from "mixpanel-browser";

import "./style.css";
import AllPatient from "../../components/mopacs-content/pages/allPatient/AllPatient";
import Study from "../../components/mopacs-content/pages/study/Study";
import { DicomUploaderManagerProvider } from "../../services/pacs-system";
import { SSEContext } from "../../utils/SSEProvider";
import { ChatSessionSharingWindow } from "../../components/anokye-content/components/anokye-sharing-window";

export default function DiagnoseChat() {
  const { theme } = useSelector((state) => state.app.uistate);
  const { user } = useSelector((state) => state.app.datastate);

  const diagnoseChatRef = useRef(null);
  const reportFormDialogRef = useRef(null);
  const reportPanelRef = useRef(null);
  const canvasPanelRef = useRef(null);
  const [isReportPanelOpen, setIsReportPanelOpen] = useState(false);
  const [isCanvasPanelOpen, setIsCanvasPanelOpen] = useState(false);
  const isMobile = useIsMobile();
  const isSidePanelOpen = isReportPanelOpen || isCanvasPanelOpen;


  // Start with sidebar collapsed on mobile, expanded on desktop
  const [isSidebarExpanded, setIsSidebarExpanded] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth >= 768; // md breakpoint
    }
    return true;
  });

  const [isRightSidebarOpen, setIsRightSidebarOpen] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth >= 1200; // Large screen, or match previous behavior (true on desktop)
    }
    return true;
  });

  // NOTE: do not call `useChatContext` here (outside the provider) — will be called inside `InnerContent`

  const SSEevents = useContext(SSEContext)

  useEffect(() => {
    if (SSEevents.commentsNotification.length > 0) {
      const latestComment = SSEevents.commentsNotification[SSEevents.commentsNotification.length - 1];
      alert(`New comment notification: ${latestComment}`);
    }
  }, [SSEevents.commentsNotification]);

  useEffect(() => {
    if (SSEevents.activeNotification.length > 0) {
      const latestActive = SSEevents.activeNotification[SSEevents.activeNotification.length - 1];
      alert(`New active notification: ${latestActive}`);
    }
  }, [SSEevents.activeNotification]);


  useHideDriftChatButton();

  // Handle responsive sidebar behavior
  useEffect(() => {
    const handleResize = () => {
      const isMobile = window.innerWidth < 768;
      // On mobile, close sidebar when resizing to mobile view
      if (isMobile && isSidebarExpanded) {
        setIsSidebarExpanded(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isSidebarExpanded]);

  useEffect(() => {
    const isLoggedIn = user?._id != null;

    if (isLoggedIn) {
      mixpanel.track("Chat Page", {
        status: "logged_in",
      });
    } else {
      mixpanel.track("Chat Page", {
        status: "not_logged_in",
      });
    }
  }, [user]);

  // Lock background scroll when the report panel is open (especially important on mobile)
  useEffect(() => {
    if (!isReportPanelOpen) return;

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [isReportPanelOpen]);

  function openReportPanel(reportSource) {
    reportPanelRef.current?.openPanel(reportSource);
  }

  useEffect(() => {
    function handleOpenReport(ev) {
      openReportPanel(ev.detail.report);
    }

    document.addEventListener(OPEN_REPORT_PANEL_EVENT, handleOpenReport);
    return () => document.removeEventListener(OPEN_REPORT_PANEL_EVENT, handleOpenReport);
  }, []);

  return (
    <UniversalChatProvider>
      <DicomUploaderManagerProvider>
        {/* InnerContent consumes chat context provided by UniversalChatProvider */}
        <InnerContentWrapper
          diagnoseChatRef={diagnoseChatRef}
          reportFormDialogRef={reportFormDialogRef}
          reportPanelRef={reportPanelRef}
          canvasPanelRef={canvasPanelRef}
          isReportPanelOpen={isReportPanelOpen}
          setIsReportPanelOpen={setIsReportPanelOpen}
          isCanvasPanelOpen={isCanvasPanelOpen}
          setIsCanvasPanelOpen={setIsCanvasPanelOpen}
          isSidebarExpanded={isSidebarExpanded}
          setIsSidebarExpanded={setIsSidebarExpanded}
          isRightSidebarOpen={isRightSidebarOpen}
          setIsRightSidebarOpen={setIsRightSidebarOpen}
          user={user}
          theme={theme}
          isMobile={isMobile}
          isSidePanelOpen={isSidePanelOpen}
        />
      </DicomUploaderManagerProvider>
    </UniversalChatProvider>
  );
}

function InnerContentWrapper({ diagnoseChatRef, reportFormDialogRef, reportPanelRef, canvasPanelRef, isReportPanelOpen, setIsReportPanelOpen, isCanvasPanelOpen, setIsCanvasPanelOpen, isSidebarExpanded, setIsSidebarExpanded, isRightSidebarOpen, setIsRightSidebarOpen, user, theme, isMobile, isSidePanelOpen }) {
  // Now it's safe to call the chat context because we're inside the provider
  const { useCreateChatSession, useActiveChatSession, useGenerateReport } = useChatContext();
  const { mutate: createNewChatSession } = useCreateChatSession();
  const { data: activeChatSession } = useActiveChatSession();
  const { mutate: generateReport } = useGenerateReport();
  const history = useHistory();
  const shareWindowRef = useRef(null);

  const [reportWidth, setReportWidth] = useState(50); // percentage
  const [splitSize, setSplitSize] = useState(() => (
    typeof window !== "undefined" ? Math.round(window.innerWidth / 2) : 500
  ));

  const openReportPanel = useCallback((reportSource) => {
    reportPanelRef.current?.openPanel(reportSource);
    // Auto-collapse sidebar when report panel opens on desktop
    if (window.innerWidth >= 768) {
      setIsSidebarExpanded(false);
    }
  }, [reportPanelRef, setIsSidebarExpanded]);

  const startGeneratingReport = useCallback((sessionId, images) => {
    reportFormDialogRef.current?.openModal(images, {
      onSubmit(data) {
        generateReport({ sessionId, input: data })
      }
    });
  }, [reportFormDialogRef, generateReport]);

  const openReportFormDialog = useCallback((image, { onSubmit }) => {
    reportFormDialogRef.current?.openModal(image, { onSubmit });
  }, [reportFormDialogRef]);

  const onToggleSidebar = useCallback(() => {
    setIsSidebarExpanded(prev => !prev);
  }, [setIsSidebarExpanded]);

  const onToggleGallery = useCallback(() => {
    setIsRightSidebarOpen(prev => !prev);
  }, [setIsRightSidebarOpen]);

  // A document gets two panes, chat and document, and the Artifacts button opens a third.
  // The list comes back for a chat with no documents, if it was open before.
  const documentCount = activeChatSession?.canvases?.length ?? 0;
  const seenDocumentCountRef = useRef(0);
  // The effect below closes the list after this render. Until then the list must not
  // count as open, or a new document would count as seen.
  const isClosingArtifacts = documentCount > seenDocumentCountRef.current;
  const reopenArtifactsRef = useRef(false);
  const hideArtifacts = () => {
    reopenArtifactsRef.current = reopenArtifactsRef.current || isRightSidebarOpen;
    setIsRightSidebarOpen(false);
  };
  useEffect(() => {
    seenDocumentCountRef.current = documentCount;
    if (documentCount > 0) hideArtifacts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentCount]);
  useEffect(() => {
    if (isCanvasPanelOpen) {
      hideArtifacts();
    } else if (reopenArtifactsRef.current && documentCount === 0) {
      reopenArtifactsRef.current = false;
      setIsRightSidebarOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCanvasPanelOpen, activeChatSession?._id]);

  useEffect(() => {
    if (history.location.pathname.includes("/anokye")) {
      setIsSidebarExpanded(false);
    }
  }, [history.location.pathname, setIsSidebarExpanded]);

  return (
    <div
      className="relative h-screen overflow-hidden bg-background"
      data-theme={theme}
      ref={diagnoseChatRef}>

      {/* Sidebar - Only rendered if user is logged in */}
      {user?._id && (
        <ChatSidebar
          isReportPanelOpen={isReportPanelOpen}
          isExpanded={isSidebarExpanded}
          onToggle={() => setIsSidebarExpanded(!isSidebarExpanded)}
          onShareSession={(session) => shareWindowRef.current?.openWindow(session)}
        />
      )}

      {/* Main Content Area - Full width on mobile, adjusted for sidebar on desktop */}
      <div
        className={`flex h-full overflow-hidden relative transition-all duration-300
          ${user?._id ? (isSidebarExpanded ? 'md:ml-64' : 'md:ml-16') : ''}
        `}
      >
        <SplitPane
          split="vertical"
          minSize={0}
          size={(!isMobile && isSidePanelOpen) ? splitSize : 0}
          onDragFinished={(size) => setSplitSize(size)}
          primary="second"
          pane1Style={{ overflow: 'hidden', display: 'flex', flexDirection: 'column', minWidth: '300px' }}
          pane2Style={{ overflow: 'hidden', display: 'flex', flexDirection: 'column' }}
          resizerStyle={{
            display: (!isMobile && isSidePanelOpen) ? 'block' : 'none',
            width: '4px',
            background: 'rgba(20, 128, 10, 0.1)',
            cursor: 'col-resize',
            zIndex: 10
          }}
          style={{
            position: 'relative',
            height: '100%',
            display: 'flex',
            flex: 1,
          }}
        >
          <ResponsiveLayoutProvider
            threshold={600}
            isReportPanelOpen={isReportPanelOpen}
            reportWidth={50} // Fixed for now as SplitPane handles actual width
            style={{
              width: '100%',
              height: '100%',
              flexShrink: 1,
            }}
          >
            {/* New Chat Header - show active chat title when available */}
            {!history.location.pathname.includes("/anokye") && (
              <ChatPageHeader
                title={(!user?._id && !activeChatSession) ? "" : (activeChatSession?.title || activeChatSession?.topic || "Untitled Chat")}
                isSidebarExpanded={isSidebarExpanded}
                onToggleSidebar={onToggleSidebar}
                onCreateChat={() => {
                  if (!user?._id) {
                    showConfirm("You need to sign in to start a new chat.", "Authentication required")
                      .then(() => history.push('/login'))
                      .catch(() => { });
                    return;
                  }
                  createNewChatSession?.();
                }}
                onShareChat={() => {
                  if (activeChatSession) {
                    shareWindowRef.current?.openWindow(activeChatSession);
                  }
                }}
              />
            )}

            {/* Mobile overlay for right sidebar */}
            {isRightSidebarOpen && (
              <div
                className="fixed inset-0 bg-black/50 z-40 md:hidden"
                onClick={() => setIsRightSidebarOpen(false)}
              />
            )}

            <div className="flex-1 overflow-y-auto relative">
              <Suspense fallback={<PageLoading />}>
                <Switch>
                  <Route
                    path="*/anokye"
                    exact
                    render={(props) => {
                      // Redirect to login if not authenticated
                      if (!user?._id) {
                        history.push(`/login?redirect=${encodeURIComponent(props.location.pathname + props.location.search)}`);
                        return <PageLoading />;
                      }
                      return (
                        <AnokyeContent
                          {...props}
                          isSidebarExpanded={isSidebarExpanded}
                          onToggleSidebar={onToggleSidebar}
                        />
                      );
                    }}
                  />
                  <Route
                    path="*/anokye/report-overview/:id"
                    render={(props) => {
                      // Redirect to login if not authenticated
                      if (!user?._id) {
                        history.push(`/login?redirect=${encodeURIComponent(props.location.pathname + props.location.search)}`);
                        return <PageLoading />;
                      }
                      return <ReportOverview {...props} />;
                    }}
                  />
                  <Route
                    path="*/anokye/report-history"
                    render={(props) => {
                      // Redirect to login if not authenticated
                      if (!user?._id) {
                        history.push(`/login?redirect=${encodeURIComponent(props.location.pathname + props.location.search)}`);
                        return <PageLoading />;
                      }
                      return <ReportHistory {...props} />;
                    }}
                  />
                  <Route
                    path="*/mopacs/allpatients"
                    render={(props) => {
                      // Redirect to login if not authenticated
                      if (!user?._id) {
                        history.push(`/login?redirect=${encodeURIComponent(props.location.pathname + props.location.search)}`);
                        return <PageLoading />;
                      }
                      return <AllPatient {...props} />;
                    }}
                  />
                  <Route
                    path="*/mopacs/study/:studyInstanceUid"
                    render={(props) => {
                      // Redirect to login if not authenticated
                      if (!user?._id) {
                        history.push(`/login?redirect=${encodeURIComponent(props.location.pathname + props.location.search)}`);
                        return <PageLoading />;
                      }
                      return <Study {...props} />;
                    }}
                  />

                  {/* Catch invalid /anokye/* and /mopacs/* sub-routes */}
                  <Route path="*/anokye/" component={NotFound} />
                  <Route path="*/mopacs/" component={NotFound} />

                  <Route
                    path="/"
                    render={(props) => (
                      <ChatContent
                        startGeneratingReport={startGeneratingReport}
                        openReportFormDialog={openReportFormDialog}
                        openReportPanel={openReportPanel}
                        onToggleSidebar={onToggleSidebar}
                        onToggleGallery={onToggleGallery}
                        isArtifactsOpen={isRightSidebarOpen && !isClosingArtifacts}
                        {...props}
                      />
                    )}
                  />
                </Switch>
              </Suspense>
            </div>
          </ResponsiveLayoutProvider>

          {/* Second pane — Report panel and Canvas panel share this slot.
              On mobile there's no room for a side-by-side split, so this
              becomes a full-screen overlay above the chat instead. */}
          <div
            className={cn(
              "flex flex-col h-full overflow-hidden",
              isMobile && isSidePanelOpen && "fixed inset-0 z-50 bg-background"
            )}
          >
            <ChatReportPanel
              onOpenPanel={() => setIsReportPanelOpen(true)}
              onClosePanel={() => setIsReportPanelOpen(false)}
              ref={reportPanelRef}
            />
            <CanvasPanel
              onOpenPanel={() => {
                setIsCanvasPanelOpen(true)
                // Auto-collapse sidebar when canvas panel opens on desktop, so the canvas gets full width
                if (window.innerWidth >= 768) {
                  setIsSidebarExpanded(false)
                }
              }}
              onClosePanel={() => setIsCanvasPanelOpen(false)}
              ref={canvasPanelRef}
            />
          </div>
        </SplitPane>

        <ChatSessionInfo
          isReportPanelOpen={isReportPanelOpen}
          isRightSidebarOpen={isRightSidebarOpen}
          onToggleRightSidebar={setIsRightSidebarOpen}
          startGeneratingReport={startGeneratingReport}
          openReportPanel={openReportPanel}
        />

      </div>

      <ReportFormDialog ref={reportFormDialogRef} />
      <ChatSessionSharingWindow ref={shareWindowRef} />
      <SourcesSidebar />
    </div>
  );
}

function useHideDriftChatButton() {
  useEffect(() => {
    let attributesObserver, previousDisplay;

    function hideNode(node) {
      previousDisplay = node.style.display;
      node.style.display = "none";

      attributesObserver = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type !== "attributes" || mutation.attributeName !== "style") return;
          mutation.target.style.display = "none";
        });
      });

      attributesObserver.observe(node, { attributes: true });
    }

    const node = document.getElementById("drift-frame-controller");

    if (node != null) {
      hideNode(node);

      return () => {
        attributesObserver?.disconnect();
        node.style.display = previousDisplay;
      };
    }

    const elementObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.addedNodes.length) {
          mutation.addedNodes.forEach((node) => {
            if (node.id !== "drift-frame-controller") return;
            hideNode(node);
          });
        }
      });
    });

    elementObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => {
      attributesObserver?.disconnect();
      elementObserver.disconnect();
    };
  }, []);
}
